import {
  buildCorrelationId
} from "./chunk-GL2JF7ZX.js";

// src/apollo/createAuthLink.ts
import { ApolloLink, Observable } from "@apollo/client";
function createAuthLink(manager, fallbackTenantId) {
  return new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      let subscriptionHandle = null;
      (async () => {
        try {
          const um = manager.getUserManager();
          const oidcUser = await um.getUser();
          const token = oidcUser?.access_token ?? null;
          const state = manager.getState();
          const rawTenant = state.user?.tenantId;
          const tenantId = rawTenant && rawTenant.trim() || fallbackTenantId || null;
          operation.setContext(
            ({ headers = {} }) => ({
              headers: {
                ...headers,
                ...token ? { Authorization: `Bearer ${token}` } : {},
                ...tenantId ? { "X-Tenant-Id": tenantId } : {}
              }
            })
          );
          subscriptionHandle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          });
        } catch (err) {
          observer.error(err);
        }
      })();
      return () => {
        subscriptionHandle?.unsubscribe();
      };
    });
  });
}
function createWsConnectionParams(manager, fallbackTenantId) {
  return async () => {
    const um = manager.getUserManager();
    const oidcUser = await um.getUser();
    const token = oidcUser?.access_token;
    const state = manager.getState();
    const rawTenant = state.user?.tenantId;
    const tenantId = rawTenant && rawTenant.trim() || fallbackTenantId || null;
    return {
      ...token ? { Authorization: `Bearer ${token}` } : {},
      ...tenantId ? { "X-Tenant-Id": tenantId } : {}
    };
  };
}

// src/apollo/createCorrelationLink.ts
import { ApolloLink as ApolloLink2 } from "@apollo/client";
function createCorrelationLink() {
  return new ApolloLink2((operation, forward) => {
    operation.setContext(
      ({ headers = {} }) => ({
        headers: {
          ...headers,
          "X-Correlation-Id": buildCorrelationId()
        }
      })
    );
    return forward(operation);
  });
}

// src/apollo/gatewayAuthority.ts
import { gql } from "@apollo/client";
var CHECK_PERMISSION_QUERY = gql`
  query AuthGatewayCheckPermission(
    $roles: [String!]!
    $strategy: PermissionStrategy!
  ) {
    checkPermission(roles: $roles, strategy: $strategy) {
      granted
      presentRoles
      requiredRoles
      matchedRoles
    }
  }
`;
var DENIED = Object.freeze({
  granted: false,
  presentRoles: [],
  requiredRoles: [],
  matchedRoles: [],
  authoritative: false
});
function normalizeRoles(roles) {
  if (!Array.isArray(roles)) return null;
  const normalized = roles.filter((role) => typeof role === "string").map((role) => role.trim()).filter(Boolean);
  if (normalized.length !== roles.length || normalized.length === 0) return null;
  return [...new Set(normalized)];
}
function stringArray(value) {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) return null;
  return value.map((item) => item.trim()).filter(Boolean);
}
function sameMembers(left, right) {
  return left.length === right.length && left.every((value) => right.includes(value));
}
function validateResult(value, requestedRoles) {
  if (!value || typeof value !== "object") return null;
  const result = value;
  if (typeof result.granted !== "boolean") return null;
  const presentRoles = stringArray(result.presentRoles);
  const requiredRoles = stringArray(result.requiredRoles);
  const matchedRoles = stringArray(result.matchedRoles);
  if (!presentRoles || !requiredRoles || !matchedRoles) return null;
  if (!sameMembers(requiredRoles, requestedRoles)) return null;
  if (matchedRoles.some((role) => !requiredRoles.includes(role))) return null;
  if (matchedRoles.some((role) => !presentRoles.includes(role))) return null;
  if (result.granted && matchedRoles.length === 0) return null;
  return {
    granted: result.granted,
    presentRoles,
    requiredRoles,
    matchedRoles,
    authoritative: true
  };
}
function createGatewayAuthorityAdapter(client, session) {
  async function evaluate(roles, strategy = "ANY_OF") {
    const requestedRoles = normalizeRoles(roles);
    if (!requestedRoles || strategy !== "ANY_OF" && strategy !== "ALL_OF") return DENIED;
    const state = session.getState();
    const user = state?.user;
    if (state?.isLoading || !state?.isAuthenticated || !user || typeof user.id !== "string" || user.id.trim() === "" || typeof user.tenantId !== "string" || user.tenantId.trim() === "" || typeof user.accessToken !== "string" || user.accessToken.trim() === "") {
      return { ...DENIED, requiredRoles: requestedRoles };
    }
    try {
      const response = await client.query({
        query: CHECK_PERMISSION_QUERY,
        variables: { roles: requestedRoles, strategy },
        fetchPolicy: "no-cache",
        errorPolicy: "all"
      });
      if (response.errors && response.errors.length > 0) {
        return { ...DENIED, requiredRoles: requestedRoles };
      }
      const evaluation = validateResult(response.data?.checkPermission, requestedRoles);
      return evaluation ?? { ...DENIED, requiredRoles: requestedRoles };
    } catch {
      return { ...DENIED, requiredRoles: requestedRoles };
    }
  }
  return {
    evaluate,
    async check(roles, strategy = "ANY_OF") {
      const result = await evaluate(roles, strategy);
      return result.authoritative && result.granted;
    }
  };
}

export {
  createAuthLink,
  createWsConnectionParams,
  createCorrelationLink,
  CHECK_PERMISSION_QUERY,
  createGatewayAuthorityAdapter
};
//# sourceMappingURL=chunk-VZUYVGSO.js.map